/**
 * Media by ad (docs/SPEC.md §14.3, §25.6; P24-T05, `backend/ads/media.py`):
 *
 * - `GET /v1/media` → `{storage, folders: [{ad_id, title, created_at,
 *   bytes, credits, items}], uploads: [...]}`, the folders newest ad first
 *   (`credits`: what the ad has cost so far, P28-T08);
 * - `DELETE /v1/media/{item_id}` → 204: an output of an ad or an upload;
 * - a folder is deleted by deleting its ad: `DELETE /v1/ads/{id}`;
 * - `POST /v1/uploads` then a PUT adds an upload (`uploads.uploadImage`).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ADS_KEY } from "./ads";
import { api } from "./client";
import { ME_KEY } from "./me";
import { uploadImage } from "./uploads";

export type MediaKind = "rt" | "final" | "still" | "upload";

export interface MediaItem {
  id: string;
  kind: MediaKind;
  name: string;
  bytes: number;
  created_at: string;
  /** The file to download: an output's attachment link or an upload's
   *  picture. */
  url: string | null;
  /** A picture to show: a true render's or an upload's. */
  thumb_url: string | null;
  language: string | null;
  deletable: boolean;
  /** An upload's pixels; null until the upload has been checked (at its
   *  first use). */
  width?: number | null;
  height?: number | null;
  mime?: string;
}

export interface MediaFolder {
  ad_id: string;
  title: string;
  created_at: string;
  bytes: number;
  /** What the ad has cost so far, in credits (§15.1, P28-T08). */
  credits?: number;
  items: MediaItem[];
}

export interface MediaStorage {
  quota_bytes: number;
  used_bytes: number;
  assetlib_bytes: number;
  studio_bytes: number;
  imports_bytes: number;
  guarantee_days: number;
  eviction_enabled: boolean;
  warned_at: string | null;
}

export interface Media {
  storage: MediaStorage;
  folders: MediaFolder[];
  uploads: MediaItem[];
}

export const MEDIA_KEY = ["media"] as const;

export function useMedia() {
  return useQuery({
    queryKey: MEDIA_KEY,
    queryFn: () => api<Media>("/v1/media"),
  });
}

function useAfterDelete() {
  const queryClient = useQueryClient();
  return () => {
    for (const queryKey of [MEDIA_KEY, ADS_KEY, ME_KEY]) {
      void queryClient.invalidateQueries({ queryKey });
    }
  };
}

/** `DELETE /v1/media/{id}`: one file. */
export function useDeleteMediaItem() {
  const after = useAfterDelete();
  return useMutation({
    mutationFn: (id: string) =>
      api<void>(`/v1/media/${encodeURIComponent(id)}`, { method: "DELETE" }),
    onSettled: after,
  });
}

/** Delete a folder = delete its ad. */
export function useDeleteFolder() {
  const after = useAfterDelete();
  return useMutation({
    mutationFn: (folder: Pick<MediaFolder, "ad_id">) =>
      api<void>(`/v1/ads/${encodeURIComponent(folder.ad_id)}`, {
        method: "DELETE",
      }),
    onSettled: after,
  });
}

/** The organisation's uploaded pictures, from Media. */
export function useUploads() {
  const media = useMedia();
  return { ...media, data: media.data?.uploads };
}

/** Ticket then bytes (`uploads.uploadImage`); Media and the balance chip's
 *  storage figures are refreshed once the upload exists. */
export function useUploadImage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => uploadImage(file),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: MEDIA_KEY });
      void queryClient.invalidateQueries({ queryKey: ME_KEY });
    },
  });
}
